import { useEffect, useMemo, useState } from 'react';
import { Title, useNotify, useTranslate } from 'react-admin';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import * as echarts from 'echarts';
import PeopleIcon from '@mui/icons-material/People';
import PercentIcon from '@mui/icons-material/Percent';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BadgeIcon from '@mui/icons-material/Badge';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PersonOffIcon from '@mui/icons-material/PersonOff';

import { API_URL, httpClient } from '../../../httpClient';
import {
  CHART_COLORS,
  ChartCard,
  DashboardHeader,
  DateRangeFilter,
  EChartsComponent,
  isoToDateInputValue,
  type RetentionDashboardData,
  StatCard,
  toBrowserDayIsoRange,
  useDefaultDateRange,
} from './shared';

const CHECKPOINT_DAYS = [30, 60, 90, 180];

/** Shown when the backend has no measurable value (never "0"). */
const NO_VALUE = '—';

export const RetentionDashboard = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const notify = useNotify();
  const { ninetyDaysAgo, today } = useDefaultDateRange();

  const [startDate, setStartDate] = useState<string>(ninetyDaysAgo);
  const [endDate, setEndDate] = useState<string>(today);
  const [data, setData] = useState<RetentionDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Guard against a slow earlier response overwriting a newer one when the
    // date range changes rapidly (no AbortController → stale-response check).
    let cancelled = false;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const queryParams = new URLSearchParams();
        const startIso = toBrowserDayIsoRange(startDate, false);
        const endIso = toBrowserDayIsoRange(endDate, true);
        if (startIso) queryParams.append('startDate', startIso);
        if (endIso) queryParams.append('endDate', endIso);

        const { json } = (await httpClient(
          `${API_URL}/job-application/dashboard/retention?${queryParams.toString()}`,
          { method: 'GET' },
        )) as { json: { data: RetentionDashboardData } };

        if (!cancelled) setData(json.data);
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching retention dashboard:', error);
        notify('ra.notification.http_error', { type: 'error' });
        setData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, notify]);

  const postHireReasonsOption = useMemo<echarts.EChartsOption>(() => {
    const points = data?.postHireTerminationReasons ?? [];
    if (!points.length) {
      return {
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: translate('resources.job-application-dashboard.charts.noData'),
            fontSize: 14,
            fill: theme.palette.text.secondary,
          },
        },
      };
    }
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary },
      },
      legend: { bottom: '5%', left: 'center', textStyle: { color: theme.palette.text.secondary } },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: theme.palette.background.paper,
            borderWidth: 3,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{d}%',
            fontSize: 12,
            fontWeight: 'bold',
            color: '#fff',
          },
          data: points.map((p, i) => ({
            name: p.name,
            value: p.value,
            itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] },
          })),
        },
      ],
    };
  }, [data, theme, translate]);

  const turnoverByCategoryOption = useMemo<echarts.EChartsOption>(() => {
    const points = data?.turnoverByCategory ?? [];
    if (!points.length) {
      return {
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: translate('resources.job-application-dashboard.charts.noData'),
            fontSize: 14,
            fill: theme.palette.text.secondary,
          },
        },
      };
    }
    // `name` is the raw closure-category enum value; map it to a friendly label.
    const labelFor = (enumValue: string): string =>
      translate(
        `resources.job-application-dashboard.dashboards.retention.turnoverCategoryLabels.${enumValue}`,
        { _: enumValue },
      );
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.divider,
        textStyle: { color: theme.palette.text.primary },
      },
      legend: { bottom: '5%', left: 'center', textStyle: { color: theme.palette.text.secondary } },
      series: [
        {
          type: 'pie',
          radius: ['40%', '70%'],
          center: ['50%', '45%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: theme.palette.background.paper,
            borderWidth: 3,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{d}%',
            fontSize: 12,
            fontWeight: 'bold',
            color: '#fff',
          },
          data: points.map((p, i) => ({
            name: labelFor(p.name),
            value: p.value,
            itemStyle: { color: CHART_COLORS[i % CHART_COLORS.length] },
          })),
        },
      ],
    };
  }, [data, theme, translate]);

  const cohorts = data?.cohorts ?? [];

  // The backend sends null when nobody was on the payroll at the start of the
  // period, so there is no denominator. Printing "0%" there would claim a
  // measured zero turnover next to a non-zero termination count.
  const turnoverHasNoBase = !isLoading && data != null && data.turnoverRate == null;

  const renderCheckpointCellContent = (cohort: (typeof cohorts)[number], days: number) => {
    const cp = cohort.checkpoints.find((c) => c.days === days);
    if (!cp) return <>-</>;
    return (
      <>
        <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
          {cp.activeCount}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {/* retentionRate already carries '%' for numeric values, or a
              textual sentinel ("Em andamento") — don't append '%'. */}
          ({cp.retentionRate})
        </Typography>
      </>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Title title={translate('resources.job-application-dashboard.dashboards.tabRetention')} />
      <DashboardHeader
        title={translate('resources.job-application-dashboard.dashboards.tabRetention')}
        dataSinceAt={data?.dataSinceAt}
      >
        <DateRangeFilter
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          minDate={isoToDateInputValue(data?.dataSinceAt)}
        />
      </DashboardHeader>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.retention.activeHeadcount')}
            value={data?.activeHeadcount ?? 0}
            icon={BadgeIcon}
            color="#22c55e"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.retention.tooltips.activeHeadcount')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.retention.tenureActives')}
            value={translate(
              'resources.job-application-dashboard.dashboards.retention.tenureActivesValue',
              {
                avg: data?.tenureActivesAvgDays ?? 0,
                median: data?.tenureActivesMedianDays ?? 0,
              },
            )}
            icon={HourglassBottomIcon}
            color="#3b82f6"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.retention.tooltips.tenureActives')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.retention.onboardingGhost')}
            value={data?.onboardingGhostCount ?? 0}
            icon={PersonOffIcon}
            color="#a855f7"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.retention.tooltips.onboardingGhost')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.retention.activeNow')}
            value={data?.activeNow ?? 0}
            icon={PeopleIcon}
            color="#22c55e"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.retention.tooltips.activeNow')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.retention.turnoverRate')}
            value={data?.turnoverRate ?? NO_VALUE}
            icon={PercentIcon}
            color="#ef4444"
            loading={isLoading}
            tooltip={translate(
              turnoverHasNoBase
                ? 'resources.job-application-dashboard.dashboards.retention.tooltips.turnoverRateNoBase'
                : 'resources.job-application-dashboard.dashboards.retention.tooltips.turnoverRate',
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <StatCard
            title={translate('resources.job-application-dashboard.dashboards.retention.avgDaysAsHired')}
            value={
              data?.avgDaysAsHiredBeforeTerminate != null
                ? data.avgDaysAsHiredBeforeTerminate.toFixed(1)
                : '0'
            }
            icon={CalendarMonthIcon}
            color="#f59e0b"
            loading={isLoading}
            tooltip={translate('resources.job-application-dashboard.dashboards.retention.tooltips.avgDaysAsHired')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <ChartCard
            title={translate('resources.job-application-dashboard.dashboards.retention.cohort')}
            tooltip={translate('resources.job-application-dashboard.dashboards.retention.tooltips.cohort')}
          >
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.retention.cohort')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {translate('resources.job-application-dashboard.dashboards.retention.totalHired')}
                    </TableCell>
                    {CHECKPOINT_DAYS.map((days) => (
                      <TableCell key={days} align="right" sx={{ fontWeight: 600 }}>
                        {translate(
                          `resources.job-application-dashboard.dashboards.retention.checkpoint${days}`,
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cohorts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2 + CHECKPOINT_DAYS.length} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          {translate('resources.job-application-dashboard.charts.noData')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    cohorts.map((row) => (
                      <TableRow key={row.cohort} hover>
                        <TableCell>{row.cohort}</TableCell>
                        <TableCell align="right">{row.totalHired}</TableCell>
                        {CHECKPOINT_DAYS.map((days) => (
                          <TableCell key={days} align="right">
                            {renderCheckpointCellContent(row, days)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title={translate('resources.job-application-dashboard.dashboards.retention.postHireReasons')}
            tooltip={translate('resources.job-application-dashboard.dashboards.retention.tooltips.postHireReasons')}
          >
            <EChartsComponent option={postHireReasonsOption} height={400} loading={isLoading} />
          </ChartCard>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ChartCard
            title={translate('resources.job-application-dashboard.dashboards.retention.turnoverByCategory')}
            tooltip={translate('resources.job-application-dashboard.dashboards.retention.tooltips.turnoverByCategory')}
          >
            <EChartsComponent option={turnoverByCategoryOption} height={400} loading={isLoading} />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};
